import { api } from "@/lib/api";
import { Capacitor } from "@capacitor/core";

function isNative() {
  try { return Capacitor?.isNativePlatform?.() === true; } catch { return false; }
}

// Public base URL for shareable links. Env-configurable; falls back to the
// current origin so preview shares preview links and production shares
// nearfluence.co.uk links automatically.
export function publicBase() {
  const env = process.env.REACT_APP_PUBLIC_BASE_URL;
  if (env && env.trim()) return env.replace(/\/$/, "");
  // In the native app window.location is capacitor://localhost — never build a
  // shareable link from that. Fall back to the configured production backend
  // origin (which is the canonical Nearfluence domain), then a safe default.
  if (isNative()) {
    const be = process.env.REACT_APP_BACKEND_URL;
    if (be && /^https:\/\//i.test(be)) return be.replace(/\/$/, "");
    return "https://nearfluence.co.uk";
  }
  return window.location.origin.replace(/\/$/, "");
}

// OG-rendered share endpoints (real WhatsApp/social previews); they redirect
// humans to the in-app SPA page.
export const opportunityShareUrl = (id) => `${publicBase()}/api/share/opportunity/${id}`;
export const creatorShareUrl = (id) => `${publicBase()}/api/share/creator/${id}`;
export const inviteUrl = (code) => `${publicBase()}/invite/${code}`;

export const inviteMessage = (url) =>
  `Join my creator network on Nearfluence 👋\nNearfluence helps creators discover collaborations, connect with other creators and work with businesses.\nJoin me here:\n${url}`;

export function whatsappHref(text, url) {
  return `https://wa.me/?text=${encodeURIComponent(url ? `${text} ${url}` : text)}`;
}
export function emailHref(subject, text, url) {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(url ? `${text}\n\n${url}` : text)}`;
}

export async function copyToClipboard(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); return true; } catch { return false; }
    finally { document.body.removeChild(ta); }
  }
}

export function canNativeShare() {
  if (isNative()) return true;
  return typeof navigator !== "undefined" && !!navigator.share;
}

export async function nativeShare({ title, text, url }) {
  // Native app: use the OS share sheet via the Capacitor Share plugin.
  if (isNative()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url, dialogTitle: title });
      return "shared";
    } catch (e) {
      return /cancel/i.test(e?.message || "") ? "cancelled" : "error";
    }
  }
  // Web: use the Web Share API when available (mobile browsers / PWA).
  if (typeof navigator !== "undefined" && navigator.share) {
    try { await navigator.share({ title, text, url }); return "shared"; }
    catch (e) { return e?.name === "AbortError" ? "cancelled" : "error"; }
  }
  return "unsupported";
}

export async function recordShare(kind, id) {
  try { await api.post("/growth/share", { kind, id }); } catch {}
}
